import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { setAppTimezone } from "@/lib/constants";
import { getBrand, saveBrand, applyBrand } from "@/lib/branding";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [loading, setLoading] = useState(true);

  useEffect(() => { applyBrand(getBrand()); }, []);

  useEffect(() => {
    if (user && user.timezone) setAppTimezone(user.timezone);
    if (user && typeof user === "object") {
      saveBrand(user);
      applyBrand({ name: user.business_name, logo: user.logo_url, invert: user.logo_invert, accent: user.accent_color });
    }
  }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.error("Logout request failed", err); }
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, setUser, loading, checkAuth, logout }), [user, loading, checkAuth, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
